import { Extension } from "@tiptap/core";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { Decoration, DecorationSet } from "@tiptap/pm/view";
import { defaultCaseBrief, defaultCaseBriefJapanese } from "../const/defaultCaseBrief";

type TPlaceholderMatchers = { whole: Set<string>; labelled: Map<string, string> };

const LABELLED = [/^\*\*(.+?):\*\*\s*(.+)$/, /^\*\*(.+?)\*\*:\s*(.+)$/];
const stripMarkdown = (text: string) => text.replace(/\*\*|__/g, "").replace(/\s+/g, " ").trim();

// The template's prompt lines, so they can be told apart from content that replaced them.
export function buildPlaceholderMatchers(templates: string[]): TPlaceholderMatchers {
  const whole = new Set<string>();
  const labelled = new Map<string, string>();
  for (const template of templates) {
    for (const raw of template.split("\n")) {
      const line = raw.trim().replace(/^[-*]\s+/, "");
      if (!line || line.startsWith("#")) continue;
      const match = LABELLED.map((pattern) => line.match(pattern)).find(Boolean);
      if (match) labelled.set(stripMarkdown(match[1]), stripMarkdown(match[2]));
      else whole.add(stripMarkdown(line));
    }
  }
  return { whole, labelled };
}

// The part of a line that is still template text: the whole line, or the prompt after a label
// ("Decision Question: What decision are you trying to make?"). Offsets are within `text`.
export function placeholderRange(text: string, matchers: TPlaceholderMatchers): [number, number] | null {
  const normalized = text.replace(/\s+/g, " ").trim();
  if (!normalized) return null;
  if (matchers.whole.has(normalized)) return [text.indexOf(normalized[0]), text.length];
  for (const [label, prompt] of matchers.labelled) {
    if (normalized.startsWith(`${label}:`) && normalized.slice(label.length + 1).trim() === prompt) {
      const start = text.lastIndexOf(prompt);
      if (start >= 0) return [start, start + prompt.length];
    }
  }
  return null;
}

const matchers = buildPlaceholderMatchers([defaultCaseBrief, defaultCaseBriefJapanese]);

// Shows the brief's untouched template prompts in grey; the text itself is unchanged. Off until
// `storage.enabled` is set, which the editor does only for organizations with document reading.
// `labelClassName`, when set, also styles the label of an untouched "Label: prompt" line.
export const TemplatePlaceholders = Extension.create<
  object,
  { enabled: boolean; className: string; labelClassName: string | null }
>({
  name: "templatePlaceholders",
  addStorage() {
    return { enabled: false, className: "text-innoveraZinc-400", labelClassName: null };
  },
  addProseMirrorPlugins() {
    const storage = this.storage;
    return [
      new Plugin({
        key: new PluginKey("templatePlaceholders"),
        props: {
          decorations(state) {
            if (!storage.enabled) return DecorationSet.empty;
            const decorations: Decoration[] = [];
            state.doc.descendants((node, pos) => {
              if (!node.isTextblock || node.type.name === "heading") return true;
              const range = placeholderRange(node.textContent, matchers);
              if (range) {
                decorations.push(Decoration.inline(pos + 1 + range[0], pos + 1 + range[1], { class: storage.className }));
                if (storage.labelClassName && range[0] > 0) {
                  decorations.push(Decoration.inline(pos + 1, pos + 1 + range[0], { class: storage.labelClassName }));
                }
              }
              return false;
            });
            return DecorationSet.create(state.doc, decorations);
          }
        }
      })
    ];
  }
});
